import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../App';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  Building2, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Loader2,
  Save,
  Plus,
  Trash2,
  Edit,
  Image,
  MessageSquare,
  Star,
  Upload,
  HelpCircle
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  if (!user) return null;

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/services', icon: Briefcase, label: 'Services' },
    { path: '/admin/team', icon: Users, label: 'Team' },
    { path: '/admin/clients', icon: Building2, label: 'Clients' },
    { path: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials' },
    { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-gray-900 text-white transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-6 border-b border-gray-800">
          <img 
            src="https://customer-assets.emergentagent.com/job_customer-portal-v2/artifacts/kd8dey33_logo_final-1_1_page-0001.jpeg" 
            alt="Logo" 
            className="h-12 mx-auto bg-white rounded p-1"
          />
          <p className="text-center text-gray-400 text-sm mt-2">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
              {location.pathname === item.path && <ChevronRight className="ml-auto" size={16} />}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <div className="px-4 py-2 mb-2">
            <p className="text-sm text-gray-400">Logged in as</p>
            <p className="text-white font-medium truncate">{user.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Dashboard Home Component
export const DashboardHome = () => {
  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <DashboardCard title="Services" value="8" icon={Briefcase} color="blue" link="/admin/services" />
        <DashboardCard title="Team Members" value="→" icon={Users} color="green" link="/admin/team" />
        <DashboardCard title="Clients" value="24" icon={Building2} color="purple" link="/admin/clients" />
        <DashboardCard title="Testimonials" value="→" icon={MessageSquare} color="yellow" link="/admin/testimonials" />
        <DashboardCard title="FAQs" value="→" icon={HelpCircle} color="cyan" link="/admin/faqs" />
        <DashboardCard title="Settings" value="→" icon={Settings} color="orange" link="/admin/settings" />
      </div>
      
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Link to="/admin/services" className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
            <Briefcase className="text-blue-600 mb-2" size={24} />
            <p className="font-medium">Manage Services</p>
            <p className="text-sm text-gray-500">Edit service details and images</p>
          </Link>
          <Link to="/admin/team" className="p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
            <Users className="text-green-600 mb-2" size={24} />
            <p className="font-medium">Manage Team</p>
            <p className="text-sm text-gray-500">Add team members with photos</p>
          </Link>
          <Link to="/admin/testimonials" className="p-4 border border-gray-200 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition-colors">
            <MessageSquare className="text-yellow-600 mb-2" size={24} />
            <p className="font-medium">Testimonials</p>
            <p className="text-sm text-gray-500">Add customer reviews</p>
          </Link>
          <Link to="/admin/faqs" className="p-4 border border-gray-200 rounded-lg hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
            <HelpCircle className="text-cyan-600 mb-2" size={24} />
            <p className="font-medium">FAQs</p>
            <p className="text-sm text-gray-500">Manage FAQ questions</p>
          </Link>
          <Link to="/admin/settings" className="p-4 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
            <Settings className="text-orange-600 mb-2" size={24} />
            <p className="font-medium">Site Settings</p>
            <p className="text-sm text-gray-500">Update contact info & stats</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

const DashboardCard = ({ title, value, icon: Icon, color, link }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    cyan: 'bg-cyan-500'
  };
  
  return (
    <Link to={link} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`${colorClasses[color]} p-3 rounded-lg`}>
          <Icon className="text-white" size={24} />
        </div>
      </div>
    </Link>
  );
};

// Services Management Component
export const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);

  // Use httpOnly cookies - withCredentials sends cookies automatically
  const axiosConfig = { withCredentials: true };

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/services`, axiosConfig);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSave = async (service) => {
    setSaving(true);
    try {
      if (service.id) {
        await axios.put(`${API}/admin/services/${service.id}`, service, axiosConfig);
      } else {
        await axios.post(`${API}/admin/services`, service, axiosConfig);
      }
      await fetchServices();
      setEditingService(null);
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, axiosConfig);
      await fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Services</h1>
        <button
          onClick={() => setEditingService({ title: '', description: '', icon: 'Briefcase', features: [], image: '' })}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> Add Service
        </button>
      </div>

      {editingService && (
        <ServiceForm 
          service={editingService} 
          onSave={handleSave} 
          onCancel={() => setEditingService(null)}
          saving={saving}
        />
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {services.map((service) => (
              <tr key={service.id}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {service.image && (
                      <img src={service.image} alt="" className="w-12 h-12 object-cover rounded" />
                    )}
                    <span className="font-medium">{service.title}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm max-w-md truncate">{service.description}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setEditingService(service)} className="text-blue-600 hover:text-blue-800 mr-3">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-800">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ServiceForm = ({ service, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(service);
  const [featuresText, setFeaturesText] = useState(service.features?.join(', ') || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      features: featuresText.split(',').map(f => f.trim()).filter(Boolean)
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">{service.id ? 'Edit Service' : 'Add New Service'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
            <select
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="Wind">Wind</option>
              <option value="Square">Square</option>
              <option value="Layers">Layers</option>
              <option value="Droplets">Droplets</option>
              <option value="ChefHat">ChefHat</option>
              <option value="Sofa">Sofa</option>
              <option value="Building2">Building2</option>
              <option value="Flame">Flame</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            rows={3}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
          <input
            type="text"
            value={form.image || ''}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            placeholder="/images/services/example.jpeg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Features (comma-separated)</label>
          <input
            type="text"
            value={featuresText}
            onChange={(e) => setFeaturesText(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            placeholder="Feature 1, Feature 2, Feature 3"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

// Team Management Component with Photo Upload
export const TeamManagement = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState(null);

  // Use httpOnly cookies - withCredentials sends cookies automatically
  const axiosConfig = { withCredentials: true };

  const fetchTeam = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/team`, axiosConfig);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/team/upload-photo`, formData, {
        ...axiosConfig,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEditingMember({ ...editingMember, image: response.data.url });
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (member) => {
    setSaving(true);
    try {
      if (member.id) {
        await axios.put(`${API}/admin/team/${member.id}`, member, axiosConfig);
      } else {
        await axios.post(`${API}/admin/team`, member, axiosConfig);
      }
      await fetchTeam();
      setEditingMember(null);
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      await axios.delete(`${API}/admin/team/${memberId}`, axiosConfig);
      await fetchTeam();
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-500 text-sm mt-1">Add your team members with photos. They'll appear on the About page.</p>
        </div>
        <button
          onClick={() => setEditingMember({ name: '', role: '', image: '', order: team.length + 1 })}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> Add Member
        </button>
      </div>

      {team.length === 0 && !editingMember && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Team Members Yet</h3>
          <p className="text-gray-500 mb-6">Add your first team member to display on the About page.</p>
          <button
            onClick={() => setEditingMember({ name: '', role: '', image: '', order: 1 })}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} /> Add Your First Team Member
          </button>
        </div>
      )}

      {editingMember && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingMember.id ? 'Edit Member' : 'Add New Member'}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(editingMember); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. John Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <input
                  type="text"
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. Director, Operations Manager"
                  required
                />
              </div>
            </div>
            
            {/* Photo Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
              <div className="flex items-start gap-4">
                {/* Photo Preview */}
                <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                  {editingMember.image ? (
                    <img src={editingMember.image} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="text-gray-400" size={32} />
                  )}
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploading ? (
                      <><Loader2 className="animate-spin" size={18} /> Uploading...</>
                    ) : (
                      <><Upload size={18} /> Upload Photo</>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">JPEG, PNG, or WebP. Max 5MB.</p>
                  
                  {/* Or paste URL */}
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">Or paste image URL:</label>
                    <input
                      type="text"
                      value={editingMember.image || ''}
                      onChange={(e) => setEditingMember({ ...editingMember, image: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setEditingMember(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-blue-700">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save Member
              </button>
            </div>
          </form>
        </div>
      )}

      {team.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map((member) => (
            <div key={member.id} className="bg-white rounded-lg shadow p-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="text-gray-400" size={32} />
                  )}
                </div>
                <h3 className="font-semibold text-lg">{member.name}</h3>
                <p className="text-gray-500">{member.role}</p>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button onClick={() => setEditingMember(member)} className="text-blue-600 hover:text-blue-800">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(member.id)} className="text-red-600 hover:text-red-800">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Clients Management Component
export const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);

  // Use httpOnly cookies - withCredentials sends cookies automatically
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, axiosConfig);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (client) => {
    setSaving(true);
    try {
      if (client.id) {
        await axios.put(`${API}/admin/clients/${client.id}`, client, axiosConfig);
      } else {
        await axios.post(`${API}/admin/clients`, client, axiosConfig);
      }
      await fetchClients();
      setEditingClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error saving client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`, axiosConfig);
      await fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Client Logos</h1>
        <button
          onClick={() => setEditingClient({ name: '', logo: '', order: clients.length + 1 })}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> Add Client
        </button>
      </div>

      {editingClient && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingClient.id ? 'Edit Client' : 'Add New Client'}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(editingClient); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input
                type="text"
                value={editingClient.name}
                onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                type="text"
                value={editingClient.logo}
                onChange={(e) => setEditingClient({ ...editingClient, logo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>
            {editingClient.logo && (
              <div className="p-4 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <img src={editingClient.logo} alt="Preview" className="h-16 object-contain" />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditingClient(null)} className="px-4 py-2 text-gray-600">Cancel</button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {clients.map((client) => (
          <div key={client.id} className="bg-white rounded-lg shadow p-4 group relative">
            <img src={client.logo} alt={client.name} className="h-16 w-full object-contain" />
            <p className="text-center text-sm text-gray-600 mt-2 truncate">{client.name}</p>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <button onClick={() => setEditingClient(client)} className="p-1 bg-blue-100 rounded text-blue-600 hover:bg-blue-200">
                <Edit size={14} />
              </button>
              <button onClick={() => handleDelete(client.id)} className="p-1 bg-red-100 rounded text-red-600 hover:bg-red-200">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Settings Management Component
export const SettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Use httpOnly cookies - withCredentials sends cookies automatically
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, axiosConfig);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, axiosConfig);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Site Settings</h1>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-2">Statistics</h2>
          <p className="text-gray-500 text-sm mb-4">These numbers appear on the homepage and about page. Years of Experience is calculated automatically from 2006 (when Extreme Clean Australia was established).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
              <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-500">
                {new Date().getFullYear() - 2006}+ (calculated automatically from 2006)
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Active Clients</label>
              <input
                type="text"
                value={settings.stats_clients}
                onChange={(e) => setSettings({ ...settings, stats_clients: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="386"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Jobs Completed This Year</label>
              <input
                type="text"
                value={settings.stats_jobs}
                onChange={(e) => setSettings({ ...settings, stats_jobs: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="1,749"
              />
              <p className="text-gray-400 text-xs mt-1">Update this quarterly with your latest job count</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Business Hours</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekday Hours</label>
              <input
                type="text"
                value={settings.hours_weekday}
                onChange={(e) => setSettings({ ...settings, hours_weekday: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekend Hours</label>
              <input
                type="text"
                value={settings.hours_weekend}
                onChange={(e) => setSettings({ ...settings, hours_weekend: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

// Testimonials Management Component
export const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [saving, setSaving] = useState(false);

  const axiosConfig = { withCredentials: true };

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`, axiosConfig);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleSave = async (testimonial) => {
    setSaving(true);
    try {
      if (testimonial.id) {
        await axios.put(`${API}/admin/testimonials/${testimonial.id}`, testimonial, axiosConfig);
      } else {
        await axios.post(`${API}/admin/testimonials`, testimonial, axiosConfig);
      }
      await fetchTestimonials();
      setEditingTestimonial(null);
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${testimonialId}`, axiosConfig);
      await fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  const toggleVisibility = async (testimonial) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonial.id}`, {
        ...testimonial,
        is_visible: !testimonial.is_visible
      }, axiosConfig);
      await fetchTestimonials();
    } catch (error) {
      console.error('Error updating testimonial:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Testimonials</h1>
          <p className="text-gray-500 text-sm mt-1">Manage customer reviews displayed on your homepage.</p>
        </div>
        <button
          onClick={() => setEditingTestimonial({ customer_name: '', company: '', review: '', rating: 5, is_visible: true })}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> Add Testimonial
        </button>
      </div>

      {testimonials.length === 0 && !editingTestimonial && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <MessageSquare className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Testimonials Yet</h3>
          <p className="text-gray-500 mb-6">Add your first customer review to display on the homepage.</p>
          <button
            onClick={() => setEditingTestimonial({ customer_name: '', company: '', review: '', rating: 5, is_visible: true })}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} /> Add Your First Testimonial
          </button>
        </div>
      )}

      {editingTestimonial && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingTestimonial.id ? 'Edit Testimonial' : 'Add New Testimonial'}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(editingTestimonial); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={editingTestimonial.customer_name}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, customer_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. John Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company (optional)</label>
                <input
                  type="text"
                  value={editingTestimonial.company || ''}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, company: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. ABC Restaurant"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review *</label>
              <textarea
                value={editingTestimonial.review}
                onChange={(e) => setEditingTestimonial({ ...editingTestimonial, review: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={4}
                placeholder="What did the customer say about your service?"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEditingTestimonial({ ...editingTestimonial, rating: star })}
                      className={`p-1 ${star <= editingTestimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      <Star size={24} fill={star <= editingTestimonial.rating ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingTestimonial.is_visible}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, is_visible: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Show on website</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setEditingTestimonial(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-blue-700">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save Testimonial
              </button>
            </div>
          </form>
        </div>
      )}

      {testimonials.length > 0 && (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className={`bg-white rounded-lg shadow p-6 ${!testimonial.is_visible ? 'opacity-60' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={16}
                          className={star <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}
                          fill={star <= testimonial.rating ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                    {!testimonial.is_visible && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Hidden</span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-3">"{testimonial.review}"</p>
                  <p className="font-medium text-gray-900">
                    {testimonial.customer_name}
                    {testimonial.company && <span className="text-gray-500 font-normal"> — {testimonial.company}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => toggleVisibility(testimonial)}
                    className={`p-2 rounded ${testimonial.is_visible ? 'text-green-600 bg-green-50' : 'text-gray-400 bg-gray-100'}`}
                    title={testimonial.is_visible ? 'Click to hide' : 'Click to show'}
                  >
                    {testimonial.is_visible ? '👁' : '👁‍🗨'}
                  </button>
                  <button onClick={() => setEditingTestimonial(testimonial)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(testimonial.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// FAQ Management Component
export const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [saving, setSaving] = useState(false);

  const axiosConfig = { withCredentials: true };

  const fetchFaqs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/faqs`, axiosConfig);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const handleSave = async (faq) => {
    setSaving(true);
    try {
      if (faq.id) {
        await axios.put(`${API}/admin/faqs/${faq.id}`, faq, axiosConfig);
      } else {
        await axios.post(`${API}/admin/faqs`, faq, axiosConfig);
      }
      await fetchFaqs();
      setEditingFaq(null);
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Error saving FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faqId}`, axiosConfig);
      await fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    }
  };

  const toggleVisibility = async (faq) => {
    try {
      await axios.put(`${API}/admin/faqs/${faq.id}`, {
        ...faq,
        is_visible: !faq.is_visible
      }, axiosConfig);
      await fetchFaqs();
    } catch (error) {
      console.error('Error updating FAQ:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">FAQs</h1>
          <p className="text-gray-500 text-sm mt-1">Manage frequently asked questions displayed on the FAQ page.</p>
        </div>
        <button
          onClick={() => setEditingFaq({ question: '', answer: '', is_visible: true })}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> Add FAQ
        </button>
      </div>

      {editingFaq && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingFaq.id ? 'Edit FAQ' : 'Add New FAQ'}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(editingFaq); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
              <input
                type="text"
                value={editingFaq.question}
                onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="e.g. What areas do you service?"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Answer *</label>
              <textarea
                value={editingFaq.answer}
                onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={4}
                placeholder="Enter the answer to this question..."
                required
              />
            </div>
            
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingFaq.is_visible}
                  onChange={(e) => setEditingFaq({ ...editingFaq, is_visible: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Show on FAQ page</span>
              </label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setEditingFaq(null)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-blue-700">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save FAQ
              </button>
            </div>
          </form>
        </div>
      )}

      {faqs.length === 0 && !editingFaq && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <HelpCircle className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No FAQs Yet</h3>
          <p className="text-gray-500 mb-6">Add your first FAQ to help answer common customer questions.</p>
          <button
            onClick={() => setEditingFaq({ question: '', answer: '', is_visible: true })}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} /> Add Your First FAQ
          </button>
        </div>
      )}

      {faqs.length > 0 && (
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={faq.id} className={`bg-white rounded-lg shadow p-6 ${!faq.is_visible ? 'opacity-60' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-gray-400 text-sm">#{index + 1}</span>
                    {!faq.is_visible && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Hidden</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleVisibility(faq)}
                    className={`p-2 rounded ${faq.is_visible ? 'text-green-600 bg-green-50' : 'text-gray-400 bg-gray-100'}`}
                    title={faq.is_visible ? 'Click to hide' : 'Click to show'}
                  >
                    {faq.is_visible ? '👁' : '👁‍🗨'}
                  </button>
                  <button onClick={() => setEditingFaq(faq)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(faq.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
